"use client";

/**
 * Schedule Now Line
 * A red horizontal "current time" line with a time chip, positioned over a single calendar day
 * column. Self-contained: it ticks its own `useNow`, so the minute update re-renders only this
 * line and not the surrounding CalendarView. Renders nothing unless the column's date is today
 * (in the event's time zone) and the current time falls within the column's visible hour range.
 */

import type React from "react";

import { formatTime } from "@/lib/utils/formatting";
import { useClientNow } from "../../../../_hooks/useNow";
import { eventTodayString, minuteOfDayInTimeZone } from "../../../../_logic/utils/schedule-utils";

interface ScheduleNowLineProps {
  /** The column's date, `YYYY-MM-DD`. The line only renders when this is today. */
  date: string;
  /** Event IANA time zone. NULL = floating time → no line. */
  timeZone: string | null;
  /** First hour rendered in the grid (e.g. 9 for a 9am start). */
  startHour: number;
  /** Pixels per hour for this grid (base on desktop, pinch-scaled on mobile). */
  hourHeightPx: number;
  /** Total grid height in px; the line is hidden when "now" falls outside it. */
  gridHeight: number;
}

/**
 * Renders the "now" line for today's column, or null.
 *
 * @param props - See ScheduleNowLineProps.
 * @returns The positioned now line, or null when not applicable.
 */
export function ScheduleNowLine({
  date,
  timeZone,
  startHour,
  hourHeightPx,
  gridHeight,
}: ScheduleNowLineProps): React.ReactElement | null {
  // Hooks
  const now = useClientNow();

  // Render
  if (now == null || timeZone == null) return null;
  if (eventTodayString(timeZone, now) !== date) return null;

  const minuteOfDay = minuteOfDayInTimeZone(now, timeZone);
  const topPx = ((minuteOfDay - startHour * 60) / 60) * hourHeightPx;
  if (topPx < 0 || topPx > gridHeight) return null;

  const label = formatTime(
    `${String(Math.floor(minuteOfDay / 60)).padStart(2, "0")}:${String(minuteOfDay % 60).padStart(2, "0")}`
  );

  return (
    <div
      // Center the row on topPx (-translate-y-1/2) so the hairline lands on the exact start-minute
      // pixel — matching how event cards anchor their top edge — instead of half-a-badge below it.
      className="pointer-events-none absolute inset-x-0 z-10 flex -translate-y-1/2 items-center"
      style={{ top: topPx }}
      aria-hidden="true"
    >
      <span className="-ml-1 shrink-0 rounded bg-red-500 px-1 py-0.5 text-[10px] font-semibold leading-none text-white">
        {label}
      </span>
      <span className="-ml-1 h-2 w-2 shrink-0 rounded-full bg-red-500" />
      <div className="h-px flex-1 bg-red-500" />
    </div>
  );
}
